import { AddIcon, CaretDownFilled, CaretUpFilled } from '@/assets/icons';
import { TDefineDocumentUploadListStore } from '@/stores/define-document-upload';
import { cn, insertObjectIfV2 } from '@/utils';
import {
	ForwardedRef,
	forwardRef,
	memo,
	useImperativeHandle,
	useMemo,
	useState,
} from 'react';

import { includes } from 'ramda';

import Button from '../common/button';
import TableWithActions from '../common/table/table-with-actions';
import { ColumnTypes } from '../common/table/type';

interface ExpandableDocumentWrapperProps {
	columns: ColumnTypes;
	dataSource: TDefineDocumentUploadListStore;
	loading?: boolean;
	containerClassName?: string;
	searchComponent?: React.ReactNode;
	addNewDocument?: (record: any) => void;
}

interface GroupedDocument {
	documentType: string;
	documents: TDefineDocumentUploadListStore;
	count: number;
}

export type ExpandableDocumentWrapperRef = {
	toggleAll: () => void;
	toggleClose: () => void;
};

const ExpandableDocumentWrapper = (
	{
		columns,
		dataSource,
		loading = false,
		containerClassName = '',
		searchComponent,
		addNewDocument,
	}: ExpandableDocumentWrapperProps,
	ref: ForwardedRef<ExpandableDocumentWrapperRef>
) => {
	const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

	useImperativeHandle(ref, () => ({
		toggleAll,
		toggleClose,
	}));

	// Group documents by documentType
	const groupedDocuments = useMemo(() => {
		return dataSource.reduce(
			(acc, document) => {
				const documentType = document.documentType as string;
				if (!acc[documentType]) {
					acc[documentType] = [];
				}
				acc[documentType].push(document);
				return acc;
			},
			{} as Record<string, TDefineDocumentUploadListStore>
		);
	}, [dataSource]);

	const groupedData: GroupedDocument[] = useMemo(() => {
		return Object.entries(groupedDocuments).map(
			([documentType, documents], index) => ({
				documentType,
				documents,
				count: documents.filter(
					(doc) => doc.fileList && Object.keys(doc.fileList).length > 0
				).length,
			})
		);
	}, [groupedDocuments]);

	const toggleGroup = (documentType: string) => {
		const newExpanded = new Set(expandedGroups);
		if (newExpanded.has(documentType)) {
			newExpanded.delete(documentType);
		} else {
			newExpanded.add(documentType);
		}
		setExpandedGroups(newExpanded);
	};

	const toggleAll = () => {
		setExpandedGroups(new Set(groupedData.map((group) => group.documentType)));
	};

	const toggleClose = () => {
		setExpandedGroups(new Set());
	};

	// Transform data to include group headers
	const transformedDataSource = useMemo(() => {
		return groupedData.flatMap((group, index) => {
			const groupHeader = {
				id: `group-${group.documentType}`,
				documentType: group.documentType, // Use documentType for grouping
				documentName: group.documents[0]?.documentName,
				isGroupHeader: true,
				count: group.count,
				expanded: expandedGroups.has(group.documentType),
			};

			const groupDocuments = expandedGroups.has(group.documentType)
				? group.documents.map((doc) => ({
						...doc,
						isGroupChild: true,
					}))
				: [];

			return [groupHeader, ...groupDocuments];
		});
	}, [groupedData, expandedGroups]);

	// Transform columns to handle group headers
	const transformedColumns = useMemo(() => {
		return columns.map((column) => ({
			...column,
			...insertObjectIfV2(column.dataIndex === 'delete', {
				className: cn(column.className, 'border-gray1 !border-r'),
			}),
			render: (value: any, record: any, index: number) => {
				if (record.isGroupHeader) {
					if (includes(column.dataIndex, ['fileName', 'registDt'])) {
						return null; // Hide these columns for group headers
					}
					// For addDocument column in group header, show the add button
					if (column.dataIndex === 'addDocument') {
						if (addNewDocument) {
							return (
								<div className="flex items-center gap-1 hover:opacity-75">
									<div
										className="cursor-pointer"
										onClick={(e) => {
											e.stopPropagation();
											addNewDocument?.(record);
										}}
									>
										<AddIcon />
									</div>
								</div>
							);
						}
					}
					if (column.dataIndex === 'typeName') {
						return (
							<div className="font-semibold text-left whitespace-pre absolute left-0 top-1/2 -translate-y-1/2 z-10">
								<span className="text-font2 mr-1">{record.documentName}</span>
								<span className="text-blue2">({record.count})</span>
							</div>
						);
					}
					if (column.dataIndex === 'delete') {
						return (
							<div
								className="h-7 flex items-center justify-center gap-2 cursor-pointer"
								onClick={() => toggleGroup(record.documentType)}
							>
								{record.expanded ? (
									<CaretDownFilled />
								) : (
									<CaretUpFilled fill="#787878" />
								)}
							</div>
						);
					}

					return column.render ? column.render(value, record, index) : value;
				}

				return column.render ? column.render(value, record, index) : value;
			},
			onCell: (record: any, index?: number) => {
				if (record?.isGroupHeader) {
					return {
						className: '!border-e-0 !cursor-auto bg-[#F9FCFF]',
					};
				}
				return {};
			},
		}));
	}, [columns, addNewDocument, toggleGroup]);

	return (
		<div className={containerClassName}>
			{/* Header Controls */}
			<div className="flex justify-between items-center pt-[15px] pb-2.5 gap-4">
				{searchComponent}
				<div className="flex gap-2">
					<Button
						className="min-w-0"
						onClick={toggleAll}
						icon={<CaretDownFilled />}
					>
						펼치기
					</Button>
					<Button
						className="min-w-0"
						onClick={toggleClose}
						icon={<CaretUpFilled fill="#787878" />}
					>
						닫기
					</Button>
				</div>
			</div>

			{/* Table */}
			<TableWithActions
				actionHidden
				columns={transformedColumns}
				dataSource={transformedDataSource}
				rowKey={'id'}
				pagination={false}
				scroll={{ y: 600 }}
				loading={loading}
				containerClassName="p-0 h-auto"
			/>
		</div>
	);
};

ExpandableDocumentWrapper.displayName = 'ExpandableDocumentWrapper';

export default memo(forwardRef(ExpandableDocumentWrapper));
